import React, { useState, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import Switch from 'react-switch';
import { HitOptions } from 'pages/Tagging/components/TaggingFlow/optionArrays/HitOptions';
import { Option } from 'pages/Tagging/components/TaggingFlow/components/Option/Option';
import { SectionHeadline } from 'pages/Tagging/components/SectionHeadline/SectionHeadline';
import { SectionHeader } from 'pages/Tagging/components/SectionHeader/SectionHeader';
import { TaggingFlowPlaceholder } from 'pages/Tagging/components/TaggingFlow/components/Placeholder/TaggingFlowPlaceholder';
import { EndOfTaggingFlowCard } from 'pages/Tagging/components/EndOfTaggingFlowCard/EndOfTaggingFlowCard';
import { BpPlay } from 'models/tagging/BpPlay';
import { BpTaggingStore } from 'stores/Tagging/BpTaggingStore';
import { useInjection } from 'services/ioc/ioc.react';
import { ServiceTypes } from 'constants/ServiceTypes';
import { HitTypes } from '@trackman/baseball-shared-components';
import { BpTaggingFlowWrapper, Header, HitTypeOffMessage, Label, PitchTypeToggle, TaggingColumn } from './BpTaggingFlowStyles';

export interface IProps {
  selectedPlay: BpPlay | undefined;
  disabled?: boolean;
}
export const BpTaggingFlow: React.FC<IProps> = observer((props: IProps) => {
  const ShouldTagHitsKey = 'bp-tag-hits';
  const { selectedPlay, disabled } = props;
  const [isFinal, setIsFinal] = useState<boolean>(false);
  const taggingStore = useInjection<BpTaggingStore>(ServiceTypes.BpTaggingStore);

  useEffect(() => {
    const item = localStorage.getItem(ShouldTagHitsKey);
    if (item) {
      taggingStore.isTaggingHits = item === 'true';
    }
  }, []);

  useEffect(() => {
    setIsFinal(!!selectedPlay && !!selectedPlay.hitType && selectedPlay.hitType !== HitTypes.Undefined);
  }, [selectedPlay, disabled]);

  const onShouldTagHitsChange = (newVal: boolean) => {
    localStorage.setItem(ShouldTagHitsKey, newVal.toString());
    taggingStore.isTaggingHits = newVal;
  };

  const onOptionSelected = (value: HitTypes) => {
    if (selectedPlay) {
      selectedPlay.hitType = value;
      selectedPlay.isChanged = true;
      setIsFinal(true);
    }
  };

  const getContent = () => {
    if (!taggingStore.isTaggingHits) {
      return <HitTypeOffMessage>Hit typing has been switched off</HitTypeOffMessage>;
    }
    return selectedPlay ? (
      <>
        <div className='options'>
          {HitOptions.map((option, index) => {
            return (
              <Option
                key={index}
                disabled={disabled}
                displayName={option.displayName}
                hotkeyCode={option.hotkeyCode}
                hotkeyDisplay={option.hotkeyDisplay}
                value={option.value}
                onPressHotkeyCallback={(value: string | number) => onOptionSelected(value as HitTypes)}
                onClickCallBack={(value: string | number) => onOptionSelected(value as HitTypes)}
                selected={selectedPlay && selectedPlay.hitType === option.value}
                isBattingPractice
              />
            );
          })}
        </div>
        {isFinal || (!selectedPlay.isChanged && selectedPlay.isValidated) ? (
          <EndOfTaggingFlowCard isTagSaved={!selectedPlay.isChanged && selectedPlay.isTagStored} />
        ) : (
          <></>
        )}
      </>
    ) : (
      <TaggingFlowPlaceholder />
    );
  };

  return (
    <BpTaggingFlowWrapper>
      <Header>
        <SectionHeadline>Tagging</SectionHeadline>
        <PitchTypeToggle>
          <Label>Hit type</Label>
          <Switch
            checked={taggingStore.isTaggingHits}
            onChange={(checked: boolean) => onShouldTagHitsChange(checked)}
            onColor='#ec691a'
            checkedIcon={false}
            uncheckedIcon={false}
            height={24.8}
            width={40.8}
            disabled={false}
          />
        </PitchTypeToggle>
      </Header>
      <TaggingColumn className='tagging-column'>
        <SectionHeader title='What was the hit type?' />
        {getContent()}
      </TaggingColumn>
    </BpTaggingFlowWrapper>
  );
});
